import { Loader2Icon } from "lucide-react"
import type * as React from "react"
import * as m from "@/paraglide/messages"
import LockBadge from "@/shared/assets/icons/lock-badge.svg?react"
import { cn } from "@/shared/lib/utils"

type Variant = "default" | "cancel" | "skip" | "black" | "blueGradient"
type Theme = "dark" | "light"

interface InlineButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: Variant
	theme?: Theme
	loading?: boolean
	showLock?: boolean
}

const variantClass = {
	default: "bg-[#282A33] text-white",
	black: "bg-black text-white",
	blueGradient:
		"bg-[linear-gradient(90deg,#43E0FB_0%,#2770FF_49.53%,#5C1CFF_100%)] text-white",
	skip: "bg-[#838DAA] text-white",
	cancel: {
		light: "border border-[#282A33] text-black",
		dark: "border border-white text-white",
	},
} as const

function getVariantClass(variant: Variant, theme: Theme) {
	if (variant === "cancel") {
		return variantClass.cancel[theme]
	}
	return variantClass[variant]
}

function SkipGlow() {
	return (
		<span
			aria-hidden="true"
			className="pointer-events-none absolute top-[93%] left-1/2 -z-10 h-[82%] w-full -translate-1/2 rounded-[50%] bg-[linear-gradient(90deg,#D7D3FB_19%,#EEE9ED_35%,#CFDAEB_50%,#C7EBED_65%,#CCE0F2_82%)] opacity-70 blur-[12px]"
		/>
	)
}

function InlineButton({
	className,
	variant = "default",
	theme = "light",
	loading = false,
	disabled,
	children,
	showLock: showLockProp,
	...rest
}: InlineButtonProps) {
	const isSkipVariant = variant === "skip"
	const showLock = Boolean(showLockProp && isSkipVariant)
	const isDisabledStyle = Boolean(disabled) && !isSkipVariant

	return (
		<div className="relative w-full">
			{showLock && (
				<div className="absolute -top-2 -right-1 z-10">
					<LockBadge className="size-7" aria-label={m.commonLocked()} />
				</div>
			)}

			<button
				type="button"
				className={cn(
					"relative isolate inline-flex h-11 w-full items-center justify-center overflow-hidden rounded-lg text-[16px] font-bold whitespace-nowrap select-none",
					isDisabledStyle
						? "bg-[#C6CBD8] text-white"
						: getVariantClass(variant, theme),
					className,
				)}
				disabled={disabled || loading}
				{...rest}
			>
				{isSkipVariant && <SkipGlow />}
				<span className="px-4">
					{loading ? (
						<Loader2Icon
							className="size-4 animate-spin"
							aria-label={m.commonLoading()}
						/>
					) : (
						children
					)}
				</span>
			</button>
		</div>
	)
}

export { InlineButton }
